import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { PageHeader } from "@shared/components/PageHeader";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

const ScheduleDetails = ({ scheduleDetails }: { scheduleDetails: any[] }) => {
  const getStatusClass = (status: string) => {
    switch (status) {
      case "Active":
        return <Badge className={getStatusBadgeClass("Active")}>Active</Badge>;
      case "Completed":
        return (
          <Badge className={getStatusBadgeClass("Completed")}>Completed</Badge>
        );
      case "Overdue":
        return (
          <Badge className={getStatusBadgeClass("Overdue")}>Overdue</Badge>
        );
      case "Paused":
        return <Badge className={getStatusBadgeClass("Paused")}>Paused</Badge>;
      case "Scheduled":
        return (
          <Badge className={getStatusBadgeClass("Scheduled")}>Scheduled</Badge>
        );
      case "Unscheduled":
        return (
          <Badge className={getStatusBadgeClass("Unscheduled")}>
            Unscheduled
          </Badge>
        );
      default:
        return <Badge variant="outline">N/A</Badge>;
    }
  };
  const scheduleDetailsFields = [
    {
      label: "Status",
      value: getStatusClass(scheduleDetails?.[0].status) || "N/A",
      tooltip: "Current operational status: Active (running/scheduled) or Inactive (paused/completed).",
    },
    { label: "Total Routes", value: scheduleDetails?.[0]?.totalRoutes || "NA", tooltip: "Number of geographical routes assigned to this read cycle." },
    {
      label: "Reading Period",
      value:
        (scheduleDetails?.[0]?.readingPeriod?.startDate || "N/A") +
        " - " +
        (scheduleDetails?.[0]?.readingPeriod?.endDate || "N/A"),
      tooltip: "The date range during which meter readings are collected for this cycle.",
    },
    {
      label: "Frequency",
      value: scheduleDetails?.[0]?.frequencyDisplay || "N/A",
      tooltip: "How often this read cycle executes automatically (e.g., Monthly, Weekly, Daily).",
    },
    { label: "Total Meters", value: scheduleDetails?.[0]?.meterCount || "NA", tooltip: "Total number of meters to be read in this cycle." },
  ];

  const administrativeDetailsFields = [
    {
      label: "Created by",
      value: scheduleDetails?.[0]?.createdUserRemoteName || "N/A",
      tooltip: "The user who originally created this schedule.",
    },
    { label: "Created date", value: scheduleDetails?.[0]?.createdDate || "N/A", tooltip: "The date when this schedule was first created in the system." },
    {
      label: "Last updated by",
      value: scheduleDetails?.[0]?.lastModifiedUserRemoteName || "N/A",
      tooltip: "The user who most recently modified this schedule.",
    },
    {
      label: "Last updated date",
      value: scheduleDetails?.[0]?.lastModifiedDate || "N/A",
      tooltip: "The date when this schedule was most recently modified in the system.",
    },
  ];
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <PageHeader
            title={scheduleDetails?.[0]?.meterCycleName || "N/A"}
            subtitle="Key schedule-level metadata for this read cycle"
          />
        </div>
      </CardHeader>
      <Card className="mt-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Schedule Details
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-3">
          {scheduleDetailsFields.map((item) => (
            <ValueLabel
              key={item.label}
              label={item.label}
              value={item.value}
              tooltip={item.tooltip}
            />
          ))}
        </div>
      </Card>
      <Card className="mt-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
           Administrative & Audit Information
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
          {administrativeDetailsFields.map((item) => (
            <ValueLabel
              key={item.label}
              label={item.label}
              value={item.value}
              tooltip={item.tooltip}
            />
          ))}
        </div>
      </Card>
    </Card>
  );
};

export default ScheduleDetails;
